/**
 * Ventilator waveform + numerics engine (GE Aisys CS2-style)
 *
 * Pure functions. Generates the three scalar ventilator waveforms
 * (airway pressure, flow, volume) for one display buffer, and computes the
 * derived numerics (Ppeak, Pplat, MV, measured Vt) from the instructor's
 * ventilator settings.
 */

// Anaesthetic agents — 1 MAC reference concentration (% at age ~40, sea level)
export const AGENTS = {
  none: { label: '—',   mac1: null },
  sevo: { label: 'SEVO', mac1: 2.0 },
  des:  { label: 'DES',  mac1: 6.0 },
  iso:  { label: 'ISO',  mac1: 1.15 },
};

// Display full-scale values used to normalise waveforms to the canvas
export const PAW_FULL_SCALE  = 40;  // cmH₂O → top of the Paw pane
export const FLOW_FULL_SCALE = 60;  // L/min → ± full scale of the Flow pane
export const VOL_FULL_SCALE  = 800; // mL    → top of the Volume pane
export const CO2_FULL_SCALE  = 50;  // mmHg  → top of the CO₂ pane

/**
 * Parse a free-form patient age into decimal years.
 * Accepts plain numbers ("55" → 55) and unit phrases used by the NRP/PALS tab
 * ("6 months" → 0.5, "28 weeks" → 0.538, "3 days" → 0.008, "4 years" → 4).
 * Returns null when empty/unparseable (caller falls back to the 40-y baseline).
 */
export function parseAgeYears(age) {
  if (age == null) return null;
  if (typeof age === 'number') return isFinite(age) ? age : null;
  const s = String(age).trim().toLowerCase();
  if (!s) return null;
  const m = s.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  if (!isFinite(n)) return null;
  if (s.includes('month')) return n / 12;
  if (s.includes('week'))  return n / 52;
  if (s.includes('day'))   return n / 365;
  return n; // bare number or "…year(s)/yr/y"
}

/**
 * Age-adjusted MAC multiplier (Mapleson). Relative to the 40-year baseline:
 * delivered MAC-equivalents scale by 10^(0.00269·(age−40)). Clamped to a sane
 * range so extreme inputs can't produce absurd factors.
 */
export function macAgeFactor(ageYears) {
  if (ageYears == null || !isFinite(ageYears)) return 1;
  const a = Math.max(0, Math.min(120, ageYears));
  return Math.pow(10, 0.00269 * (a - 40));
}

/** Parse an "I:E" string ("1:2", "1:1.5", "2:1") into the inspiratory fraction. */
export function inspFraction(ie) {
  const [i, e] = String(ie).split(':').map(parseFloat);
  if (!i || !e) return 1 / 3;
  return i / (i + e);
}

// ─── Lung mechanics model ─────────────────────────────────────────────────────
// The patient's lungs are described by a static COMPLIANCE (mL/cmH₂O) and an
// airway RESISTANCE (cmH₂O·s/L). A scenario's vent block {vte, ppeak, pplat}
// is converted into C and R once (deriveMechanics); after that the ventilator
// behaves like a ventilator: changing VT, rate, PEEP or I:E recomputes the
// pressures from the lungs (pressuresFromMechanics) instead of leaving the
// instructor's hand-typed numbers frozen. Bronchodilators lower R, PTX
// decompression restores C, etc., via the same two numbers.

/** Inspiratory flow (L/s) for a VCV breath: VT over the inspiratory time. */
export function inspiratoryFlow(settings) {
  const rr = Math.max(1, settings.rr ?? 12);
  const ti = (60 / rr) * inspFraction(settings.ie ?? '1:2');
  return Math.max(0.05, (settings.vte ?? 500) / 1000 / Math.max(0.2, ti));
}

/** Compliance + resistance implied by the current pressures. */
export function deriveMechanics(settings) {
  const peep = settings.peep ?? 5;
  const driving = Math.max(1, (settings.pplat ?? peep + 10) - peep);
  const resistive = Math.max(0, (settings.ppeak ?? settings.pplat ?? 0) - (settings.pplat ?? 0));
  return {
    compliance: +(Math.max(2, (settings.vte ?? 500) / driving)).toFixed(1),
    resistance: +(resistive / inspiratoryFlow(settings)).toFixed(1),
  };
}

/** Pressures produced by the current settings on lungs with the given C and R. */
export function pressuresFromMechanics(settings) {
  const peep = settings.peep ?? 5;
  const C = Math.max(2, settings.compliance ?? deriveMechanics(settings).compliance);
  const R = Math.max(0, settings.resistance ?? deriveMechanics(settings).resistance);
  const pplat = Math.round(peep + (settings.vte ?? 500) / C);
  const ppeak = Math.round(pplat + R * inspiratoryFlow(settings));
  return { pplat: Math.min(80, pplat), ppeak: Math.min(80, ppeak) };
}

/**
 * Apply one setting change with physiological coupling:
 *   • VT / rate / PEEP / I:E → pressures follow the lungs (C, R fixed)
 *   • Ppeak / Pplat typed directly → the lungs are re-fitted to those pressures
 */
export function applyVentSettingChange(settings, key, value) {
  const next = { ...settings, [key]: value };
  if (['vte', 'rr', 'peep', 'ie'].includes(key)) {
    const mech = { compliance: settings.compliance, resistance: settings.resistance, ...(settings.compliance == null ? deriveMechanics(settings) : {}) };
    const p = pressuresFromMechanics({ ...next, ...mech });
    return { ...next, ...mech, ...p, pplat: Math.max(next.peep, p.pplat), ppeak: Math.max(Math.max(next.peep, p.pplat), p.ppeak) };
  }
  if (key === 'ppeak' || key === 'pplat') {
    if (next.pplat > next.ppeak) next[key === 'ppeak' ? 'pplat' : 'ppeak'] = value;
    if (next.peep > next.pplat) next.peep = next.pplat;
    return { ...next, ...deriveMechanics(next) };
  }
  return next;
}

/**
 * Assemble ventilator numerics. VTe, Ppeak and Pplat are directly set by the
 * instructor; MV is derived from VTe × rate.
 * @returns {{vt,ppeak,pplat,peep,mv,rr,fio2,ie,mode}}
 */
export function computeVentNumerics(settings, ageYears = null) {
  const { mode, peep, rr, fio2, ie, vte, ppeak, pplat, fico2, agent, agentPct,
          psupport, flowTrigger, totalFlow, n2o } = settings;
  const agentRef = AGENTS[agent] ?? AGENTS.none;
  // Age-adjusted MAC: the same delivered agent % yields more MAC-equivalents
  // in older patients (Mapleson). Baseline age 40 → factor 1.
  const ageFactor = macAgeFactor(ageYears);
  const macAge = ageYears == null ? 40 : Math.round(Math.max(0, Math.min(120, ageYears)));
  // N₂O is delivered as a % of fresh gas; MAC of N₂O ≈ 104 %, so it contributes
  // (N₂O% / 104) MAC-equivalents, additive with the volatile agent (MAC adds up).
  const n2oPct = Math.max(0, Math.min(100, n2o ?? 0));
  const macAgent = agentRef.mac1 ? ((agentPct || 0) / agentRef.mac1) * ageFactor : 0;
  const macN2O = (n2oPct / 104) * ageFactor;
  const mac = +(macAgent + macN2O).toFixed(1);
  const inspFrac = inspFraction(ie);
  // Mean airway pressure ≈ PEEP + driving pressure weighted by inspiratory time
  const pmean = Math.round(peep + (ppeak - peep) * (inspFrac * 0.85 + 0.05));
  // Dynamic compliance = exhaled tidal volume / driving pressure
  const driving = Math.max(1, pplat - peep);
  const compliance = Math.round(vte / driving);
  return {
    mode,
    vt: Math.round(vte),
    ppeak: Math.round(ppeak),
    pplat: Math.round(pplat),
    pmean,
    peep: Math.round(peep),
    mv: +(vte * rr / 1000).toFixed(1),
    rr: Math.round(rr),
    ie,
    inspFrac,
    fio2: Math.round(fio2),
    fico2: Math.round(fico2 ?? 0),
    agent: agent ?? 'none',
    agentLabel: agentRef.label,
    agentPct: agentPct ?? 0,
    n2o: Math.round(n2oPct),          // FiN₂O ≈ delivered %
    etN2o: Math.round(n2oPct * 0.95), // equilibrates fast; ET slightly below FI
    mac,
    macAge,
    compliance,
    resistance: +(settings.resistance ?? deriveMechanics(settings).resistance).toFixed(1),
    psupport: Math.round(psupport ?? 0),
    flowTrigger: flowTrigger ?? 0,
    totalFlow: totalFlow ?? 0,
  };
}

// Ventilator alarm thresholds (cmH₂O / mL / L·min⁻¹).
export const VENT_ALARM_LIMITS = {
  ppeakWarn: 40, ppeakCrisis: 50,   // high airway pressure
  vtLow: 250,                        // low delivered tidal volume
  mvLow: 3.0,                        // low minute ventilation (L/min)
};

/**
 * Evaluate ventilator numerics against airway-pressure / volume limits.
 * Returns alarm objects shaped like the vitals alarms so they render in the
 * shared AlarmBar. High Paw is the headline derangement for obstructive and
 * low-compliance scenarios; low VTe / MV flag failure to ventilate.
 * @param {object} num  output of computeVentNumerics
 */
export function evaluateVentAlarms(num) {
  const out = [];
  if (num.ppeak >= VENT_ALARM_LIMITS.ppeakCrisis) {
    out.push({ vitalName: 'Paw High', level: 'crisis', value: num.ppeak, unit: 'cmH₂O' });
  } else if (num.ppeak >= VENT_ALARM_LIMITS.ppeakWarn) {
    out.push({ vitalName: 'Paw High', level: 'warning', value: num.ppeak, unit: 'cmH₂O' });
  }
  if (num.vt < VENT_ALARM_LIMITS.vtLow) {
    out.push({ vitalName: 'TVexp Low', level: 'warning', value: num.vt, unit: 'mL' });
  }
  if (num.mv < VENT_ALARM_LIMITS.mvLow) {
    out.push({ vitalName: 'MV Low', level: 'warning', value: num.mv, unit: 'L/min' });
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ventilator → vitals physiological coupling
//
// EtCO₂ is inversely proportional to alveolar minute ventilation (for a fixed
// CO₂ production): raising RR or VTe blows off CO₂; lowering retains it.
// SpO₂ rises with FiO₂ — but ONLY to the degree the lung responds. Pure shunt
// (perfused, unventilated lung) and dyshaemoglobin/arrest do not respond.
// ═══════════════════════════════════════════════════════════════════════════

// How much SpO₂ improves per 1% FiO₂ at full responsiveness (linear teaching
// approximation of the sigmoid PaO₂/FiO₂ relationship, clamped at 100%).
const SPO2_PER_FIO2 = 0.2;

/**
 * New EtCO₂ target after a change in minute ventilation (MV = VTe × RR).
 * @param {number} etco2Target current EtCO₂ target (mmHg)
 * @param {number} mvOld  minute ventilation before the change (L/min)
 * @param {number} mvNew  minute ventilation after the change (L/min)
 */
export function coupledEtco2(etco2Target, mvOld, mvNew) {
  if (mvNew <= 0.1) return Math.min(99, etco2Target * 3);   // near-apnoea → CO₂ climbs
  const next = etco2Target * (mvOld / mvNew);
  return Math.round(Math.max(8, Math.min(99, next)));
}

/**
 * New SpO₂ target after a change in FiO₂, scaled by the scenario's oxygenation
 * responsiveness (1 = full V/Q-mismatch response, 0 = pure shunt / no response).
 * @param {number} spo2Target current SpO₂ target (%)
 * @param {number} fio2Old  FiO₂ before the change (%)
 * @param {number} fio2New  FiO₂ after the change (%)
 * @param {number} responsiveness 0–1
 */
export function coupledSpo2(spo2Target, fio2Old, fio2New, responsiveness) {
  const delta = (fio2New - fio2Old) * SPO2_PER_FIO2 * responsiveness;
  return Math.round(Math.max(20, Math.min(100, spo2Target + delta)));
}

// Per-scenario/modifier oxygenation responsiveness to FiO₂ (0 = shunt / no
// response, 1 = fully responsive). Anything not listed uses DEFAULT_FIO2_RESPONSE
// (typical V/Q mismatch or hypoventilation — FiO₂ helps well).
export const DEFAULT_FIO2_RESPONSE = 0.7;
export const FIO2_RESPONSE = {
  // No ventilation reaching alveoli → FiO₂ irrelevant
  laryngospasmComplete: 0.0,
  epicDifficultAirway: 0.0,
  obFailedIntubation: 0.0,
  // Cardiac arrest / non-pulsatile flow → SpO₂ not O₂-responsive
  vf: 0.0, epicVFVT: 0.0, epicAsystolePEA: 0.0,
  obgynCardiacArrest: 0.0, palsVfArrest: 0.0, palsAsystole: 0.0, palsPEA: 0.0,
  // True shunt — perfused but unventilated lung (poor FiO₂ response)
  epicHypoxemia: 0.15, epicPneumothorax: 0.15, palsTensionPneumo: 0.15,
  palsPneumonia: 0.1, obAspiration: 0.2, obPulmonaryEdema: 0.2,
  obgynPeripartumCM: 0.15, obgynPeripartumCM_flash: 0.15, obgynSCAD: 0.2,
  amnioticFluidEmbolism: 0.2, airEmbolism: 0.25, epicPulmonaryEmbolism: 0.25,
  epicRightHeartFailure: 0.3,
  // Partial obstruction / mixed — moderate response
  bronchospasm: 0.55, epicBronchospasm: 0.55, laryngospasmPartial: 0.5,
  anaphylaxis: 0.45, epicAnaphylaxis: 0.45,
  // Classic V/Q mismatch / hypoventilation — strong response
  hypoxia: 0.85,
  // Dyshaemoglobinaemias — SpO₂ does not track true oxygenation, no FiO₂ response
  methemoglobinemia: 0.0,   // SpO₂ stuck ~85% regardless of FiO₂
  carbonMonoxide: 0.0,      // SpO₂ falsely normal; unchanged by FiO₂
};
// Modifiers that impose a shunt independent of the active scenario
export const FIO2_RESPONSE_MODIFIER = {
  tensionPneumothorax: 0.15,
};

/** Oxygenation responsiveness to FiO₂ for the active scenario/modifier. */
export function fio2Responsiveness(activeScenario, activeModifier) {
  if (activeModifier && activeModifier in FIO2_RESPONSE_MODIFIER) {
    return FIO2_RESPONSE_MODIFIER[activeModifier];
  }
  if (activeScenario && activeScenario in FIO2_RESPONSE) {
    return FIO2_RESPONSE[activeScenario];
  }
  return DEFAULT_FIO2_RESPONSE;
}

/**
 * Generate one display buffer of a ventilator waveform.
 *
 * @param {'paw'|'flow'|'volume'} channel
 * @param {object} settings   ventilator settings
 * @param {number} sampleRate Hz
 * @param {number} bufferDuration seconds
 * @returns {Float32Array} normalised samples — paw/volume in [0,1] (baseline
 *          at bottom), flow in [-1,1] (baseline centred)
 */
export function generateVentBuffer(channel, settings, sampleRate, bufferDuration) {
  const n = Math.ceil(bufferDuration * sampleRate);
  const out = new Float32Array(n);

  const { mode, peep, rr, ie } = settings;
  const period = 60 / Math.max(1, rr);
  const inspFrac = inspFraction(ie);
  const inspTime = period * inspFrac;
  const expTime = period - inspTime;

  const num = computeVentNumerics(settings);
  const { ppeak, pplat, vt } = num;

  // Peak inspiratory flow (L/min)
  const inspFlow = mode === 'VCV'
    ? (vt / 1000 / inspTime) * 60
    : (vt / 1000 / inspTime) * 60 * 1.6; // PCV starts higher, decelerates

  for (let i = 0; i < n; i++) {
    const t = (i / sampleRate) % period;
    const inInsp = t < inspTime;
    const ti = inInsp ? t / inspTime : 0;            // 0..1 within inspiration
    const te = inInsp ? 0 : (t - inspTime) / expTime; // 0..1 within expiration

    let value;
    if (channel === 'paw') {
      let paw;
      if (mode === 'VCV') {
        if (inInsp) {
          // Fast rise to Ppeak (first 18%), then ease down to Pplat
          paw = ti < 0.18
            ? peep + (ppeak - peep) * (ti / 0.18)
            : ppeak - (ppeak - pplat) * ((ti - 0.18) / 0.82);
        } else {
          paw = peep + (pplat - peep) * Math.exp(-te * 4);
        }
      } else {
        // PCV — square pressure waveform
        if (inInsp) {
          paw = ti < 0.05 ? peep + (ppeak - peep) * (ti / 0.05) : ppeak;
        } else {
          paw = peep + (pplat - peep) * Math.exp(-te * 5);
        }
      }
      value = paw / PAW_FULL_SCALE;

    } else if (channel === 'flow') {
      let flow;
      if (inInsp) {
        flow = mode === 'VCV'
          ? inspFlow                                  // constant (square)
          : inspFlow * Math.exp(-ti * 3);             // decelerating (PCV)
      } else {
        // Passive expiratory flow — negative, exponential decay
        flow = -inspFlow * 1.1 * Math.exp(-te * 4);
      }
      value = flow / FLOW_FULL_SCALE;

    } else if (channel === 'co2') {
      // Capnograph — CO₂ rises during EXPIRATION (vent expiratory phase),
      // drops to baseline (FiCO₂) at the next mechanical inspiration.
      const etco2 = settings.etco2 ?? 38;
      const fico2v = settings.fico2 ?? 0;
      const shoulder = etco2 * 0.93;
      let co2;
      if (inInsp) {
        co2 = ti < 0.08 ? etco2 - (etco2 - fico2v) * (ti / 0.08) : fico2v;
      } else {
        co2 = te < 0.12
          ? fico2v + (shoulder - fico2v) * (te / 0.12)            // rapid upstroke
          : shoulder + (etco2 - shoulder) * ((te - 0.12) / 0.88); // sloping plateau
      }
      value = co2 / CO2_FULL_SCALE;

    } else { // volume
      let vol;
      if (inInsp) {
        vol = mode === 'VCV'
          ? vt * ti                                   // linear fill
          : vt * (1 - Math.exp(-ti * 3));             // exponential fill (PCV)
      } else {
        vol = vt * Math.exp(-te * 4);                 // passive emptying
      }
      value = vol / VOL_FULL_SCALE;
    }

    out[i] = value;
  }

  return out;
}
